import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Typography, Button } from 'antd';

const { Title, Paragraph } = Typography;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darkenColor(hex, amount = 0.2) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

export default function SectionHero({ slug, section, siteInfo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = section.content_data || {};
  const brandColor = data.brand_color || siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const heroTitle = data.hero_title || section.title || siteInfo?.hero_title;
  const heroSubtitle = data.hero_subtitle || section.subtitle || siteInfo?.hero_subtitle;
  const ctaText = section.config?.hero_cta_text || data.hero_cta_text || siteInfo?.hero_cta_text || t('sections.hero.defaultCta');
  const ctaLink = section.config?.hero_cta_link || data.hero_cta_link || siteInfo?.hero_cta_link || 'contact';
  const trustBadges = section.config?.trust_badges || [];

  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';
  const lum = getLuminance(brandColor);
  const needsDarken = lum > 0.55;
  const darkBrand = needsDarken ? darkenColor(brandColor, 0.3) : brandColor;

  const heroBg = isMinimal
    ? '#ffffff'
    : isClassic
    ? `linear-gradient(180deg, ${darkBrand}, ${darkenColor(darkBrand, 0.12)})`
    : `linear-gradient(135deg, ${darkBrand}, ${darkenColor(darkBrand, 0.2)})`;

  const titleColor = isMinimal ? '#1a1a2e' : '#fff';
  const subtitleColor = isMinimal ? '#555' : 'rgba(255,255,255,0.9)';
  const padding = isMinimal ? '60px 24px' : isClassic ? '70px 24px 60px' : '80px 24px 60px';

  const btnBg = isMinimal ? brandColor : '#fff';
  const btnColor = isMinimal ? '#fff' : darkBrand;
  const btnBorder = isMinimal ? `2px solid ${brandColor}` : 'none';
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : 24;

  return (
    <div style={{
      background: heroBg,
      color: titleColor,
      padding,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: isMinimal ? `3px solid ${brandColor}` : 'none',
    }}>
      {(data.hero_image_url || siteInfo?.hero_image_url) && !isMinimal && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${data.hero_image_url || siteInfo?.hero_image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2,
        }} />
      )}
      <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
        <Title level={1} style={{
          color: titleColor,
          marginBottom: 12,
          fontSize: isMinimal ? 36 : isClassic ? 38 : 40,
          lineHeight: 1.2,
          letterSpacing: isMinimal ? 2 : isClassic ? 1 : 0,
          fontWeight: isMinimal ? 300 : 700,
        }}>
          {heroTitle}
        </Title>
        {heroSubtitle && (
          <Paragraph style={{
            color: subtitleColor,
            fontSize: isMinimal ? 16 : 18,
            marginBottom: 32,
            lineHeight: 1.6,
          }}>
            {heroSubtitle}
          </Paragraph>
        )}
        <Button
          type="primary" size="large"
          onClick={() => {
            if (ctaLink.startsWith('#')) {
              const el = document.getElementById(ctaLink.slice(1)) || document.getElementById(`section-${ctaLink.slice(1)}`);
              if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
            }
            navigate(`/s/${slug}/${ctaLink}`);
          }}
          style={{
            background: btnBg,
            color: btnColor,
            border: btnBorder,
            borderRadius: btnRadius,
            fontWeight: 600,
            height: 48,
            padding: '0 32px',
            fontSize: 16,
          }}
        >
          {ctaText}
        </Button>
        {trustBadges.length > 0 && (
          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
            {trustBadges.map((badge, i) => (
              <span key={i} style={{
                background: isMinimal ? `${brandColor}12` : 'rgba(255,255,255,0.18)',
                color: isMinimal ? brandColor : '#fff',
                padding: '6px 16px',
                borderRadius: isMinimal ? 0 : isClassic ? 4 : 20,
                fontSize: 13,
                border: isMinimal ? `1px solid ${brandColor}30` : 'none',
              }}>
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
